/*
 * AURA STORE — Controlled Scroll Physics v1.1.0
 *
 * Goal:
 * - desktop wheel / trackpad damping without scroll snap;
 * - small gestures remain precise;
 * - large flicks are progressively compressed;
 * - a short interpolation adds weight without obvious latency;
 * - touch/mobile stays native;
 * - nested scroll areas, dialogs and horizontal rails stay independent.
 *
 * Scope: homepage only (.template-index).
 */

(function () {
  'use strict';

  if (
    window.AuraScrollPhysics &&
    window.AuraScrollPhysics.version
  ) {
    return;
  }


  var root =
    document.documentElement;

  var body =
    document.body;


  var desktopQuery =
    window.matchMedia(
      '(min-width: 990px) and (hover: hover) and (pointer: fine)'
    );


  var reducedMotionQuery =
    window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    );


  var CONFIG = {

    /* Small movements remain almost direct */
    smallThreshold: 24,

    smallMultiplier: 0.92,


    /* Progressive compression for medium / large flicks */
    curvePower: 0.78,

    curveGain: 1.60,


    /* Maximum accepted distance per wheel event */
    maxEventDelta: 160,


    /* Additional damping during dense trackpad bursts */
    burstWindow: 48,

    burstMultiplier: 0.88,


    /* Maximum distance targetScroll can sit ahead */
    maxLeadVh: 0.72,

    minLead: 420,

    maxLead: 760,


    /* Interpolation strength */
    lerp: 0.145,


    /* Stop threshold */
    settleDistance: 0.35

  };


  var state = {

    enabled: false,

    current:
      window.scrollY || 0,

    target:
      window.scrollY || 0,

    raf: 0,

    lastFrame: 0,

    lastWheelAt: 0,

    lastDirection: 0

  };


  /*
   * Areas that should preserve their own native scrolling.
   */
  var nativeSelectors = [

    '[data-aura-scroll-native]',

    '.aura-drawer',

    '.aura-drawer__body',

    '.aura-filter-dialog',

    '.aura-filter-dialog__body',

    '[data-aura-filter-dialog]',

    'dialog',

    '[role="dialog"]',

    '[aria-modal="true"]',

    '.aura-cookie',

    '.aura-promo'

  ].join(',');


  var navigationKeys = {

    ArrowUp: true,

    ArrowDown: true,

    PageUp: true,

    PageDown: true,

    Home: true,

    End: true,

    ' ': true

  };


  /* ============================================================
     UTILITIES
     ============================================================ */

  function clamp(
    value,
    min,
    max
  ) {

    return Math.max(
      min,
      Math.min(
        max,
        value
      )
    );

  }


  function maxScroll() {

    return Math.max(
      0,
      document.documentElement.scrollHeight -
      window.innerHeight
    );

  }


  function syncToNative() {

    var y =
      clamp(

        window.scrollY ||
        window.pageYOffset ||
        0,

        0,

        maxScroll()

      );


    state.current =
      y;


    state.target =
      y;

  }


  function cancelAnimation(
    sync
  ) {

    if (
      state.raf
    ) {

      window.cancelAnimationFrame(
        state.raf
      );


      state.raf =
        0;

    }


    state.lastFrame =
      0;


    if (
      sync !== false
    ) {

      syncToNative();

    }

  }


  /* ============================================================
     ACTIVATION
     ============================================================ */

  function isHomepage() {

    return !!(
      document.body &&
      document.body.classList.contains(
        'template-index'
      )
    );

  }


  function shouldEnable() {

    return (
      isHomepage() &&
      desktopQuery.matches &&
      !reducedMotionQuery.matches
    );

  }


  /* ============================================================
     WHEEL NORMALIZATION
     ============================================================ */

  function normalizeWheelDelta(
    event
  ) {

    var delta =
      Number(
        event.deltaY
      ) || 0;


    /*
     * deltaMode:
     *
     * 0 = pixels
     * 1 = lines
     * 2 = pages
     */

    if (
      event.deltaMode === 1
    ) {

      delta *=
        16;

    }

    else if (
      event.deltaMode === 2
    ) {

      delta *=
        Math.max(
          1,
          window.innerHeight * 0.9
        );

    }


    return delta;

  }


  /* ============================================================
     DELTA COMPRESSION
     ============================================================ */

  function compressDelta(
    delta,
    now
  ) {

    var sign =
      delta < 0
        ? -1
        : 1;


    var amount =
      Math.abs(
        delta
      );


    var output;


    /*
     * Small movement:
     * keep it almost direct.
     */
    if (
      amount <=
      CONFIG.smallThreshold
    ) {

      output =
        amount *
        CONFIG.smallMultiplier;

    }


    /*
     * Medium / large movement:
     * progressively compress it.
     */
    else {

      var base =
        CONFIG.smallThreshold *
        CONFIG.smallMultiplier;


      var excess =
        amount -
        CONFIG.smallThreshold;


      output =
        base +
        Math.pow(
          excess,
          CONFIG.curvePower
        ) *
        CONFIG.curveGain;

    }


    /*
     * Trackpads send many events during one flick.
     *
     * Add a little extra damping when events arrive densely.
     */
    if (
      state.lastWheelAt &&
      now -
      state.lastWheelAt <
      CONFIG.burstWindow &&
      amount >
      CONFIG.smallThreshold
    ) {

      output *=
        CONFIG.burstMultiplier;

    }


    /*
     * Absolute protection against abnormal peaks.
     */
    output =
      Math.min(
        output,
        CONFIG.maxEventDelta
      );


    return (
      output *
      sign
    );

  }


  /* ============================================================
     NESTED VERTICAL SCROLL AREAS
     ============================================================ */

  function canScrollVertically(
    element,
    delta
  ) {

    if (
      !element ||
      element === document.body ||
      element === root
    ) {

      return false;

    }


    var style =
      window.getComputedStyle(
        element
      );


    var overflowY =
      style.overflowY;


    if (
      overflowY !== 'auto' &&
      overflowY !== 'scroll' &&
      overflowY !== 'overlay'
    ) {

      return false;

    }


    if (
      element.scrollHeight <=
      element.clientHeight + 2
    ) {

      return false;

    }


    /*
     * Scrolling down.
     */
    if (
      delta > 0
    ) {

      return (
        element.scrollTop +
        element.clientHeight <
        element.scrollHeight - 1
      );

    }


    /*
     * Scrolling up.
     */
    if (
      delta < 0
    ) {

      return (
        element.scrollTop >
        1
      );

    }


    return false;

  }


  function hasNativeScrollableAncestor(
    target,
    delta
  ) {

    var node =
      target &&
      target.nodeType === 1

        ? target

        : target &&
          target.parentElement;


    while (
      node &&
      node !== document.body &&
      node !== root
    ) {

      if (
        canScrollVertically(
          node,
          delta
        )
      ) {

        return true;

      }


      node =
        node.parentElement;

    }


    return false;

  }


  /* ============================================================
     BYPASS CONDITIONS
     ============================================================ */

  function shouldBypass(
    event,
    delta
  ) {

    if (
      !state.enabled
    ) {

      return true;

    }


    if (
      event.defaultPrevented
    ) {

      return true;

    }


    /*
     * Pinch zoom can be emitted as Ctrl + wheel.
     */
    if (
      event.ctrlKey
    ) {

      return true;

    }


    /*
     * Preserve explicit horizontal wheel navigation.
     */
    if (
      event.shiftKey
    ) {

      return true;

    }


    /*
     * Horizontal trackpad gesture.
     *
     * Important for:
     *
     * - Actualités;
     * - product rails;
     * - carousels;
     * - horizontal collections.
     */
    if (
      Math.abs(
        event.deltaX
      ) >
      Math.abs(
        event.deltaY
      ) * 0.72
    ) {

      return true;

    }


    /*
     * Main theme lock.
     */
    if (
      document.body.classList.contains(
        'is-locked'
      ) ||
      root.hasAttribute(
        'data-aura-scroll-suspended'
      )
    ) {

      return true;

    }


    var target =
      event.target;


    /*
     * Explicitly protected components.
     */
    if (
      target &&
      target.closest &&
      target.closest(
        nativeSelectors
      )
    ) {

      return true;

    }


    /*
     * Preserve native form interactions.
     */
    if (
      target &&
      target.closest &&
      target.closest(
        [
          'select',
          'textarea',
          'input[type="number"]',
          '[contenteditable="true"]'
        ].join(',')
      )
    ) {

      return true;

    }


    /*
     * Preserve any genuinely scrollable internal container.
     */
    if (
      hasNativeScrollableAncestor(
        target,
        delta
      )
    ) {

      return true;

    }


    return false;

  }


  /* ============================================================
     TARGET LEAD CAP
     ============================================================ */

  function getLeadCap() {

    return clamp(

      window.innerHeight *
      CONFIG.maxLeadVh,

      CONFIG.minLead,

      CONFIG.maxLead

    );

  }


  /* ============================================================
     RAF
     ============================================================ */

  function requestTick() {

    if (
      state.raf
    ) {

      return;

    }


    state.lastFrame =
      performance.now();


    state.raf =
      window.requestAnimationFrame(
        tick
      );

  }


  function tick(
    now
  ) {

    state.raf =
      0;


    /*
     * Immediately stop when page scrolling becomes suspended.
     */
    if (
      !state.enabled ||
      document.body.classList.contains(
        'is-locked'
      ) ||
      root.hasAttribute(
        'data-aura-scroll-suspended'
      )
    ) {

      cancelAnimation(
        true
      );


      return;

    }


    var dt =
      clamp(

        now -
        (
          state.lastFrame ||
          now
        ),

        8,

        34

      );


    state.lastFrame =
      now;


    /*
     * Frame-rate-independent interpolation.
     */
    var alpha =
      1 -
      Math.pow(

        1 -
        CONFIG.lerp,

        dt /
        16.667

      );


    var distance =
      state.target -
      state.current;


    /*
     * Finished.
     */
    if (
      Math.abs(
        distance
      ) <=
      CONFIG.settleDistance
    ) {

      state.current =
        state.target;


      window.scrollTo(
        0,
        Math.round(
          state.current
        )
      );


      state.lastFrame =
        0;


      return;

    }


    /*
     * Interpolate rendered position towards target.
     */
    state.current +=
      distance *
      alpha;


    window.scrollTo(
      0,
      state.current
    );


    state.raf =
      window.requestAnimationFrame(
        tick
      );

  }


  /* ============================================================
     MAIN WHEEL CONTROLLER
     ============================================================ */

  function onWheel(
    event
  ) {

    var rawDelta =
      normalizeWheelDelta(
        event
      );


    if (
      !rawDelta
    ) {

      return;

    }


    if (
      shouldBypass(
        event,
        rawDelta
      )
    ) {

      return;

    }


    var now =
      performance.now();


    /*
     * Resync after an idle pause.
     */
    if (
      !state.raf &&
      now -
      state.lastWheelAt >
      160
    ) {

      syncToNative();

    }


    var direction =
      rawDelta < 0
        ? -1
        : 1;


    /*
     * Direction changes should be immediate.
     *
     * Drop leftover virtual momentum when user reverses direction.
     */
    if (
      state.lastDirection &&
      direction !==
      state.lastDirection &&
      now -
      state.lastWheelAt <
      140
    ) {

      state.target =
        state.current;

    }


    var delta =
      compressDelta(
        rawDelta,
        now
      );


    state.lastDirection =
      direction;


    state.lastWheelAt =
      now;


    var lead =
      getLeadCap();


    var nextTarget =
      clamp(

        state.target +
        delta,

        0,

        maxScroll()

      );


    /*
     * Maximum lead cap.
     *
     * A violent flick cannot queue several screens ahead
     * of the position actually rendered.
     */
    state.target =
      clamp(

        nextTarget,

        Math.max(
          0,
          state.current -
          lead
        ),

        Math.min(
          maxScroll(),
          state.current +
          lead
        )

      );


    /*
     * Replace native vertical wheel movement.
     */
    event.preventDefault();


    requestTick();

  }


  /* ============================================================
     NATIVE SCROLL SYNC
     ============================================================ */

  function onNativeScroll() {

    /*
     * During our RAF, window.scrollTo triggers scroll events.
     *
     * Only use native position as source of truth
     * when custom animation is inactive.
     */
    if (
      !state.raf
    ) {

      syncToNative();

    }

  }


  /* ============================================================
     KEYBOARD
     ============================================================ */

  function onKeyDown(
    event
  ) {

    if (
      navigationKeys[
        event.key
      ]
    ) {

      /*
       * Keyboard stays native.
       */
      cancelAnimation(
        true
      );

    }

  }


  /* ============================================================
     POINTER
     ============================================================ */

  function onPointerDown() {

    /*
     * Direct interaction becomes authoritative.
     *
     * Useful for:
     *
     * - scrollbar drag;
     * - clicking quickly after a flick;
     * - UI interactions.
     */
    cancelAnimation(
      true
    );

  }


  /* ============================================================
     ANCHORS
     ============================================================ */

  function onAnchorClick(
    event
  ) {

    var link =
      event.target &&
      event.target.closest

        ? event.target.closest(
            'a[href*="#"]'
          )

        : null;


    if (
      !link
    ) {

      return;

    }


    /*
     * Browser anchor behavior stays native.
     */
    cancelAnimation(
      true
    );

  }


  /* ============================================================
     HISTORY / RESTORATION
     ============================================================ */

  function onPageRestore() {

    window.requestAnimationFrame(
      function () {

        cancelAnimation(
          true
        );

      }
    );

  }


  /* ============================================================
     RESIZE / BREAKPOINT
     ============================================================ */

  function onResize() {

    if (
      !shouldEnable()
    ) {

      disable();

      return;

    }


    if (
      !state.enabled
    ) {

      enable();

      return;

    }


    state.target =
      clamp(
        state.target,
        0,
        maxScroll()
      );


    state.current =
      clamp(
        state.current,
        0,
        maxScroll()
      );

  }


  /* ============================================================
     ENABLE
     ============================================================ */

  function enable() {

    if (
      state.enabled ||
      !shouldEnable()
    ) {

      return;

    }


    state.enabled =
      true;


    /*
     * base.css:
     *
     * html.aura-scroll-physics {
     *   scroll-behavior:auto;
     * }
     *
     * avoids double smoothing.
     */
    root.classList.add(
      'aura-scroll-physics'
    );


    syncToNative();

  }


  /* ============================================================
     DISABLE
     ============================================================ */

  function disable() {

    if (
      !state.enabled
    ) {

      return;

    }


    state.enabled =
      false;


    root.classList.remove(
      'aura-scroll-physics'
    );


    cancelAnimation(
      true
    );

  }


  /* ============================================================
     REFRESH
     ============================================================ */

  function refresh() {

    if (
      shouldEnable()
    ) {

      enable();


      cancelAnimation(
        true
      );

    }

    else {

      disable();

    }

  }


  /* ============================================================
     DESTROY
     ============================================================ */

  function destroy() {

    disable();


    window.removeEventListener(
      'wheel',
      onWheel
    );


    window.removeEventListener(
      'scroll',
      onNativeScroll
    );


    window.removeEventListener(
      'resize',
      onResize
    );


    window.removeEventListener(
      'pageshow',
      onPageRestore
    );


    window.removeEventListener(
      'popstate',
      onPageRestore
    );


    window.removeEventListener(
      'hashchange',
      onPageRestore
    );


    document.removeEventListener(
      'keydown',
      onKeyDown
    );


    document.removeEventListener(
      'pointerdown',
      onPointerDown
    );


    document.removeEventListener(
      'click',
      onAnchorClick,
      true
    );


    document.removeEventListener(
      'shopify:section:load',
      refresh
    );


    if (
      bodyObserver
    ) {

      bodyObserver.disconnect();

    }


    if (
      rootObserver
    ) {

      rootObserver.disconnect();

    }

  }


  /* ============================================================
     MEDIA QUERY COMPATIBILITY
     ============================================================ */

  function addMediaListener(
    query,
    handler
  ) {

    if (
      query.addEventListener
    ) {

      query.addEventListener(
        'change',
        handler
      );

    }

    else if (
      query.addListener
    ) {

      query.addListener(
        handler
      );

    }

  }


  /* ============================================================
     EVENTS
     ============================================================ */

  window.addEventListener(
    'wheel',
    onWheel,
    {
      passive: false
    }
  );


  window.addEventListener(
    'scroll',
    onNativeScroll,
    {
      passive: true
    }
  );


  window.addEventListener(
    'resize',
    onResize,
    {
      passive: true
    }
  );


  window.addEventListener(
    'pageshow',
    onPageRestore
  );


  window.addEventListener(
    'popstate',
    onPageRestore
  );


  window.addEventListener(
    'hashchange',
    onPageRestore
  );


  document.addEventListener(
    'keydown',
    onKeyDown
  );


  document.addEventListener(
    'pointerdown',
    onPointerDown,
    {
      passive: true
    }
  );


  document.addEventListener(
    'click',
    onAnchorClick,
    true
  );


  /*
   * Shopify Theme Editor:
   *
   * resync existing instance,
   * do not create another engine.
   */
  document.addEventListener(
    'shopify:section:load',
    refresh
  );


  addMediaListener(
    desktopQuery,
    refresh
  );


  addMediaListener(
    reducedMotionQuery,
    refresh
  );


  /* ============================================================
     BODY LOCK OBSERVER
     ============================================================ */

  var bodyObserver =
    new MutationObserver(
      function () {

        if (
          document.body.classList.contains(
            'is-locked'
          )
        ) {

          cancelAnimation(
            true
          );

        }

      }
    );


  bodyObserver.observe(
    document.body,
    {

      attributes: true,

      attributeFilter: [
        'class'
      ]

    }
  );


  /* ============================================================
     HEADER / OVERLAY SUSPENSION OBSERVER
     ============================================================ */

  var rootObserver =
    new MutationObserver(
      function () {

        if (
          root.hasAttribute(
            'data-aura-scroll-suspended'
          )
        ) {

          cancelAnimation(
            true
          );

        }

        else {

          syncToNative();

        }

      }
    );


  rootObserver.observe(
    root,
    {

      attributes: true,

      attributeFilter: [
        'data-aura-scroll-suspended'
      ]

    }
  );


  /* ============================================================
     PUBLIC API
     ============================================================ */

  window.AuraScrollPhysics = {

    version:
      '1.1.0',


    config:
      CONFIG,


    refresh:
      refresh,


    destroy:
      destroy,


    /*
     * Used by the header before opening/closing overlays.
     */
    sync:
      function () {

        cancelAnimation(
          true
        );

      }

  };


  /* ============================================================
     INITIALIZE
     ============================================================ */

  refresh();

})();